/**
 * One-shot viewer geolocation grant for the "Get directions" routing path
 * (CU-86ajd3yt0, epic CU-86ajd302x).
 *
 * Clones the scanner geolocation-grant UX from the emergency-access landing page
 * (mideo-cloud-functions `renderLandingPage.ts`, CU-86ahjxqh6): feature-detect
 * first, fire `getCurrentPosition` only on an explicit tap, handle grant vs.
 * denied/unavailable/timeout, same request options.
 *
 * KEY DIFFERENCE — no persistence. On the scan page the fix IS the product
 * (beaconed server-side). Here the viewer's coordinate is a transient means to
 * one end: building the OSM `route=from;to` directions URL client-side. It is
 * NEVER beaconed, written to Firestore, or sent to MIDEO — it goes straight into
 * the map link the viewer opens and is otherwise discarded. This is only needed
 * on desktop web (OSM won't guess an origin); native maps apps supply their own
 * origin, so this helper isn't called there.
 *
 * "Ask once until cleared": the BROWSER remembers the per-origin grant natively,
 * so a second tap won't re-prompt. Callers may additionally cache the resolved
 * coordinate for the session to skip even the (permission-free) re-fetch.
 */

export type ViewerLocation = { lat: number; lng: number };

/** Same request options as the scan-page grant (CU-86ahjxqh6). */
const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

/** True when the browser exposes the geolocation API (gate the button on this). */
export function geolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

/**
 * Request the viewer's current position once. Resolves with `{ lat, lng }` on
 * grant; REJECTS on denied / unavailable / timeout (caller falls back to the
 * plain view handoff). Never persists the coordinate.
 */
export function requestViewerLocation(): Promise<ViewerLocation> {
  return new Promise((resolve, reject) => {
    if (!geolocationAvailable()) {
      reject(new Error('geolocation-unavailable'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      // Denied, unavailable, or timed out — all one failure to the caller.
      (err) => reject(err),
      GEO_OPTIONS,
    );
  });
}
